import * as React from 'react';

/**
 * Tiny before/after JSON diff view for the audit-trail expanded row.
 *
 * Why hand-rolled vs. jsondiffpatch: the audit detail panel surfaces two
 * JSON blobs per row; a heavyweight diff library brings its own CSS and
 * 30+kB of bundle for a feature that one engineer might glance at once a
 * week. The view here renders the two payloads side-by-side as pretty-
 * printed JSON, with simple shallow key-level highlighting (added /
 * removed / changed). When the audit screen graduates to a power-user tool
 * (T-0117 follow-up), swap to `jsondiffpatch` without touching this
 * primitive's caller surface.
 */

export interface AuditDiffProps {
  before: unknown;
  after: unknown;
  className?: string;
}

export function AuditDiff({ before, after, className }: AuditDiffProps) {
  const beforeStr = React.useMemo(() => prettyJson(before), [before]);
  const afterStr = React.useMemo(() => prettyJson(after), [after]);

  const changedKeys = React.useMemo(() => diffTopLevelKeys(before, after), [before, after]);

  return (
    <div className={className}>
      {changedKeys.added.length + changedKeys.removed.length + changedKeys.changed.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1 text-meta">
          {changedKeys.added.map((k) => (
            <span
              key={`a-${k}`}
              className="rounded-chip border border-posture-healthy/40 bg-posture-healthy-tint px-2 py-0.5 font-mono text-posture-healthy"
            >
              +{k}
            </span>
          ))}
          {changedKeys.removed.map((k) => (
            <span
              key={`r-${k}`}
              className="rounded-chip border border-posture-critical/40 bg-posture-critical-tint px-2 py-0.5 font-mono text-posture-critical"
            >
              -{k}
            </span>
          ))}
          {changedKeys.changed.map((k) => (
            <span
              key={`c-${k}`}
              className="rounded-chip border border-posture-at-risk/40 bg-posture-at-risk-tint px-2 py-0.5 font-mono text-posture-at-risk"
            >
              ~{k}
            </span>
          ))}
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <div className="mb-1 text-meta font-medium uppercase tracking-wide text-ink-500">
            Before
          </div>
          <pre className="max-h-72 overflow-auto rounded-card border border-ink-100 bg-paper-50 p-3 font-mono text-mono-sm text-ink-700">
            {beforeStr}
          </pre>
        </div>
        <div>
          <div className="mb-1 text-meta font-medium uppercase tracking-wide text-ink-500">
            After
          </div>
          <pre className="max-h-72 overflow-auto rounded-card border border-ink-100 bg-paper-50 p-3 font-mono text-mono-sm text-ink-700">
            {afterStr}
          </pre>
        </div>
      </div>
    </div>
  );
}

function prettyJson(v: unknown): string {
  if (v === null || v === undefined) return '— null —';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/**
 * Shallow key-level diff. We don't go deep because a) audit payloads are
 * usually flat already and b) deep diffs warrant the real library.
 */
function diffTopLevelKeys(
  before: unknown,
  after: unknown,
): { added: string[]; removed: string[]; changed: string[] } {
  const b = isObject(before) ? before : {};
  const a = isObject(after) ? after : {};
  const beforeKeys = new Set(Object.keys(b));
  const afterKeys = new Set(Object.keys(a));
  const added: string[] = [];
  const removed: string[] = [];
  const changed: string[] = [];
  for (const k of afterKeys) {
    if (!beforeKeys.has(k)) {
      added.push(k);
    } else if (JSON.stringify(b[k]) !== JSON.stringify(a[k])) {
      changed.push(k);
    }
  }
  for (const k of beforeKeys) {
    if (!afterKeys.has(k)) {
      removed.push(k);
    }
  }
  added.sort();
  removed.sort();
  changed.sort();
  return { added, removed, changed };
}

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}
